import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { FileDown, Loader2, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { DateRangePicker } from '../components/ui/date-range-picker';

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeRow(row) {
  return {
    ...row,
    total_calls: Number(row?.total_calls || 0),
    gemma_transcribed_calls: Number(row?.gemma_transcribed_calls || 0),
    gemma_analysed_calls: Number(row?.gemma_analysed_calls || 0),
    claude_transcribed_calls: Number(row?.claude_transcribed_calls || 0),
    claude_analysed_calls: Number(row?.claude_analysed_calls || 0),
    avg_score: toNumberOrNull(row?.avg_score),
    avg_grammar: toNumberOrNull(row?.avg_grammar),
    avg_professionalism: toNumberOrNull(row?.avg_professionalism),
    avg_empathy: toNumberOrNull(row?.avg_empathy),
    avg_communication: toNumberOrNull(row?.avg_communication),
    avg_duration: toNumberOrNull(row?.avg_duration),
    escalations: Number(row?.escalations || 0),
    resolution_rate: toNumberOrNull(row?.resolution_rate),
    critical_calls: Number(row?.critical_calls || 0),
    tags_count: Number(row?.tags_count || 0),
  };
}

export default function ReportsPage() {
  const [report, setReport] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({ from_date: '', to_date: '', agent_number: '' });

  useEffect(() => {
    api.getAgentsList().then(res => setAgents(res.agents || [])).catch(() => { });
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      // Reports cover both processing pipelines. total_calls plus the Gemma
      // (gemini_*) and Claude (transcript/processing_status) transcribed/analysed
      // counts and all scores are computed server-side. Only call getReport here — never
      // couple the table to a second request (a failed sibling call in a
      // Promise.all used to blank the whole table even when the report loaded).
      const res = await api.getReport(filters);
      const rows = Array.isArray(res) ? res : (res?.report || []);
      setReport(rows.map(normalizeRow));
      setGeneratedAt(Array.isArray(res) ? null : (res?.generated_at || null));
      setHasGenerated(true);
    } catch (err) {
      const message = err?.message || 'Failed to generate report';
      toast.error(message);
      setFetchError(message);
      setReport([]);
      setGeneratedAt(null);
      setHasGenerated(true);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const blob = await api.exportCSV(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agent_performance_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Agent performance reports & export</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Report Filters</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Date Range</label>
            <DateRangePicker
              value={{ from_date: filters.from_date, to_date: filters.to_date }}
              onChange={({ from_date, to_date }) =>
                setFilters(prev => ({ ...prev, from_date, to_date }))
              }
              buttonClassName="w-full min-w-0"
              data-testid="reports-date-range"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Agent</label>
            <select
              value={filters.agent_number}
              onChange={(e) => setFilters({ ...filters, agent_number: e.target.value })}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Agents</option>
              {agents.map(a => {
                const num = typeof a === 'object' ? a.agent_number : a;
                const name = typeof a === 'object' ? (a.agent_name || a.agent_number) : a;
                return <option key={num} value={num}>{name}</option>;
              })}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setFilters({ from_date: '', to_date: '', agent_number: '' })} data-testid="reports-clear-filters-btn" className="h-9 px-14 text-sm">
              Clear
            </Button>
            <Button onClick={fetchReport} disabled={loading} className="flex-1">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />}
              Generate
            </Button>
            <Button variant="secondary" onClick={handleExportCSV} disabled={exporting || report.length === 0} data-testid="reports-export-button">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Report Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="reports-preview-table">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating report…
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Agent</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Calls</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Gemma Analyzed</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Claude Analyzed</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Avg Score</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Grammar</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Professional</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Empathy</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Communication</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Escalations</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Critical</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {report.map((r) => (
                    <tr key={r.agent_number || r.agent_name} className="border-b border-border/50 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-sm font-medium">{r.agent_name || r.agent_number}</td>
                      <td className="px-4 py-3 text-sm tabular-nums">{r.total_calls}</td>
                      <td className="px-4 py-3 text-sm tabular-nums">{r.gemma_analysed_calls}</td>
                      <td className="px-4 py-3 text-sm tabular-nums">{r.claude_analysed_calls}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          r.avg_score == null
                            ? 'bg-muted/50 text-muted-foreground'
                            : r.avg_score >= 75 ? 'score-good'
                            : r.avg_score >= 60 ? 'score-review'
                            : 'score-critical'
                        }`}>
                          {r.avg_score != null ? r.avg_score.toFixed(1) : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums">{r.avg_grammar != null ? r.avg_grammar.toFixed(1) : '-'} /10</td>
                      <td className="px-4 py-3 text-sm tabular-nums">{r.avg_professionalism != null ? r.avg_professionalism.toFixed(1) : '-'} /10</td>
                      <td className="px-4 py-3 text-sm tabular-nums">{r.avg_empathy != null ? r.avg_empathy.toFixed(1) : '-'} /10</td>
                      <td className="px-4 py-3 text-sm tabular-nums">{r.avg_communication != null ? r.avg_communication.toFixed(1) : '-'} /10</td>
                      <td className="px-4 py-3 text-sm tabular-nums">{r.escalations}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${r.critical_calls > 0 ? 'bg-rose-500/15 text-rose-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
                          {r.critical_calls}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums">{r.tags_count}</td>
                    </tr>
                  ))}
                  {report.length === 0 && (
                    <tr>
                      <td colSpan={12} className={`text-center py-10 text-sm ${fetchError ? 'text-rose-300' : 'text-muted-foreground'}`}>
                        {fetchError
                          ? `Could not load report: ${fetchError}`
                          : hasGenerated
                            ? 'No data found for the selected criteria.'
                            : 'Select filters and click Generate to view the report.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {generatedAt && (
              <div className="px-4 py-2 border-t border-border">
                <p className="text-xs text-muted-foreground">Generated: {new Date(generatedAt).toLocaleString()}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
